import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue';

export function useAntTableHorizontalScroll(containerRef, options = {}) {
    const scrollStep = options.scrollStep ?? 150;
    const hoverScrollStep = options.hoverScrollStep ?? 10;
    const hoverScrollIntervalMs = options.hoverScrollIntervalMs ?? 16;
    const edgeThreshold = options.edgeThreshold ?? 2;
    const selector = options.selector ?? '.ant-table-body';
    const additionalSelector= '.ant-table-content';

    const canScrollLeft = ref(false);
    const canScrollRight = ref(false);
    const hasHorizontalScroll = ref(false);

    const boundScrollEl = ref(null);
    let hoverScrollTimeout = null;
    let hoverScrollInterval = null;
    let resizeObserver = null;

    const getScrollEl = () => {
        const container = containerRef.value;
        const tableRoot = container?.querySelector('.ant-table-wrapper');
        if (!tableRoot) return null;

        const candidates = Array.from(
            tableRoot.querySelectorAll(`${selector}, ${additionalSelector}`)
        ).filter((element) => element.closest('.ant-table-wrapper') === tableRoot);

        return candidates.find((element) => element.scrollWidth > element.clientWidth)
            ?? candidates[0]
            ?? null;
    };

    const setBoundScrollEl = (el) => {
        if (boundScrollEl.value === el) return;

        if (boundScrollEl.value) {
            boundScrollEl.value.removeEventListener('scroll', updateScrollState);
        }

        boundScrollEl.value = el;
        boundScrollEl.value?.addEventListener('scroll', updateScrollState);
    };

    const updateScrollState = () => {
        const el = getScrollEl();

        if (!el) {
            canScrollLeft.value = false;
            canScrollRight.value = false;
            hasHorizontalScroll.value = false;
            return;
        }

        const left = el.scrollLeft;
        const clientWidth = el.clientWidth;
        const scrollWidth = el.scrollWidth;

        const maxScrollLeft = Math.max(0, scrollWidth - clientWidth);

        canScrollLeft.value = left > edgeThreshold;
        canScrollRight.value = maxScrollLeft - left > edgeThreshold;
        hasHorizontalScroll.value = maxScrollLeft > edgeThreshold;
    };

    const bind = async () => {
        await nextTick();

        const el = getScrollEl();
        if (!el) return;

        setBoundScrollEl(el);
        updateScrollState();
    };

    const unbind = () => {
        if (boundScrollEl.value) {
            boundScrollEl.value.removeEventListener('scroll', updateScrollState);
            boundScrollEl.value = null;
        }
    };

    const rebind = async () => {
        unbind();
        await bind();
    };

    const scrollBy = (left, behavior = 'smooth') => {
        const el = getScrollEl();
        if (!el) return;

        setBoundScrollEl(el);

        const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
        const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, el.scrollLeft + left));
        const targetScrollLeft = Math.abs(maxScrollLeft - nextScrollLeft) <= edgeThreshold
            ? maxScrollLeft
            : nextScrollLeft;

        el.scrollTo({
            left: targetScrollLeft,
            behavior,
        });
    };

    const scrollLeft = () => {
        scrollBy(-scrollStep);
    };

    const scrollRight = () => {
        scrollBy(scrollStep);
    };

    const stopHoverScroll = () => {
        if (hoverScrollTimeout) {
            clearTimeout(hoverScrollTimeout);
            hoverScrollTimeout = null;
        }

        if (!hoverScrollInterval) return;

        clearInterval(hoverScrollInterval);
        hoverScrollInterval = null;
    };

    const startHoverScroll = (direction) => {
        stopHoverScroll();

        const scroll = () => {
            if (direction === 'left' && canScrollLeft.value) {
                scrollBy(-hoverScrollStep, 'auto');
                return;
            }

            if (direction === 'right' && canScrollRight.value) {
                scrollBy(hoverScrollStep, 'auto');
                return;
            }

            stopHoverScroll();
        };

        hoverScrollTimeout = setTimeout(() => {
            hoverScrollTimeout = null;
            scroll();
            hoverScrollInterval = setInterval(scroll, hoverScrollIntervalMs);
        }, 250);
    };

    onMounted(() => {
        window.addEventListener('resize', updateScrollState);
        resizeObserver = new ResizeObserver(rebind);
        if (containerRef.value) resizeObserver.observe(containerRef.value);
        bind();
    });

    onBeforeUnmount(() => {
        stopHoverScroll();
        resizeObserver?.disconnect();
        unbind();
        window.removeEventListener('resize', updateScrollState);
    });

    return {
        showLeft: computed(() => hasHorizontalScroll.value && canScrollLeft.value),
        showRight: computed(() => hasHorizontalScroll.value && canScrollRight.value),
        scrollLeft,
        scrollRight,
        startHoverScroll,
        stopHoverScroll,
        updateScrollState,
        bindScroll: bind,
        unbindScroll: unbind,
        rebindScroll: rebind,
    };
}
